import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';
import { LoadScript } from '@react-google-maps/api';

import API from './Lib/Requests';
import AuthService from './Services/AuthService';
import AccountService from './Services/AccountService';
import LocalStorageService from './Services/LocalStorageService';
import OrgService from './Services/OrganizationService';

import ErrorBoundary from './Components/ErrorBoundary';
import { BrandPrimarySpinningCircle } from './Components/LoadingCircle';
import Routes from './Routes';

import { BrandingProvider } from './Context/BrandingContext';
import { BreakpointProvider } from './Context/BreakpointContext';
import { CurrentOrgProvider } from './Context/CurrentOrgContext';
import { CurrentUserProvider } from './Context/CurrentUserContext';
import { ExitSubOrgProvider } from './Context/ExitSubOrgContext';
import { HandleSubOrgDeleteProvider } from './Context/HandleSubOrgDeleteContext';
import { IconsProvider } from './Context/IconsContext';
import { OrgsMapProvider } from './Context/OrgsMapContext';
import { PermissionsProvider } from './Context/PermissionsContext';
import { ResetGlobalStateProvider } from './Context/ResetGlobalStateContext';
import { RootOrgProvider } from './Context/RootOrgContext';
import { SetCurrentOrgProvider } from './Context/SetCurrentOrgContext';
import { ToggleSidebarContextProvider } from './Context/ToggleSidebarContext';
import { UpdateCurrentOrgProvider } from './Context/UpdateCurrentOrgContext';
import { UpdateCurrentUserProvider } from './Context/UpdateCurrentUserContext';
import { UserProvider } from './Context/UserContext';
import { WidthProvider } from './Context/WidthContext';

import alarmPin from './Static/map-pins/alarm.png';
import alertHeaderDesktop from './Static/new_alert_header_desktop.svg';
import alertHeaderMobile from './Static/new_alert_header.svg';
import assetIconRed from './Static/AssetIconRed.png';
import assetIconWhite from './Static/Asset_Icon_White.png';
import assetModelIcon from './Static/AssetModel_Icon.png';
import assetModelIconWhite from './Static/AssetModel_Icon_White.png';
import avatar from './Static/Profile_Icon.png';
import certificateIcon from './Static/Certificate_Icon.png';
import chartIcon from './Static/Chart_Icon.png';
import commandControlIcon from './Static/CommandControl_Icon.png';
import conditionsIcon from './Static/Conditions_Icon.png';
import cumminsBannerDesktop from './Static/cummins_banner_desktop.svg';
import cumminsBannerMobile from './Static/cummins_banner_mobile.svg';
import cumminsIconBlack from './Static/cummins_logo_black.png';
import cumminsIconWhite from './Static/cummins_logo_white.png';
import emptyStateIcon from './Static/Icon_EmptyState.svg';
import engineIcon from './Static/car-engine.svg';
import errorPageIcon from './Static/404_Icon.png';
import eventsIcon from './Static/Events_Icon_White.png';
import fleetIcon from './Static/FleetOverview_Icon.png';
import geofencingIconBlue from './Static/GeofencingBlue_Icon.png';
import geofencingIconWhite from './Static/GeofencingWhite_Icon.png';
import groupPin from './Static/map-pins/group.png';
import maintenanceIconEmpty from './Static/MaintenanceSchedule_EmptyIcon.png';
import maintenanceIconWhite from './Static/MaintenanceSchedule_Icon_White.png';
import mapClusterIcon from './Static/map-marker-clusters/small.png';
import missingImage from './Static/missing.png';
import modbusIcon from './Static/Modbus_Icon.png';
import offlinePin from './Static/map-pins/offline.png';
import onlinePin from './Static/map-pins/default.png';
import orgIcon from './Static/Organization_Icon.png';
import orgIconWhite from './Static/Organization_Icon_White.png';
import partsIconBlue from './Static/PartsBlue_Icon.png';
import partsIconGrey from './Static/PartsGrey_Icon.png';
import primaryLinkIcon from './Static/primary_link.png';
import profileIcon from './Static/Profile_Icon.png';
import provisionIcon from './Static/AssetProvisioningIcon.png';
import provisionIconWhite from './Static/AssetProvisioningIcon_White.png';
import pumpIcon from './Static/pump-icon.png';
import reminderIcon from './Static/Reminder_Icon.png';
import reportingIcon from './Static/Reporting_Icon.png';
import searchIcon from './Static/Search_Icon.png';
import searchIconLg from './Static/Search_Icon_Large.png';
import signalIcon from './Static/datapoint.png';
import signalIconWhite from './Static/Signal_Icon_White.png';
import usersIcon from './Static/Users_Icon.png';
import usersIconWhite from './Static/Users_Icon_White.png';
import widgetIcon from './Static/widget-icon.png';

import {
  LARGE_WIDTH_PX,
  MAIN_HEIGHT,
  MOBILE_MAIN_HEIGHT,
} from './Styles/constants';

const images = {
  alarmPin,
  alertHeaderDesktop,
  alertHeaderMobile,
  assetIconRed,
  assetIconWhite,
  assetModelIcon,
  assetModelIconWhite,
  avatar,
  certificateIcon,
  chartIcon,
  commandControlIcon,
  conditionsIcon,
  cumminsBannerDesktop,
  cumminsBannerMobile,
  cumminsIconBlack,
  cumminsIconWhite,
  emptyStateIcon,
  engineIcon,
  errorPageIcon,
  eventsIcon,
  fleetIcon,
  geofencingIconBlue,
  geofencingIconWhite,
  groupPin,
  maintenanceIconEmpty,
  maintenanceIconWhite,
  mapClusterIcon,
  missingImage,
  modbusIcon,
  offlinePin,
  onlinePin,
  orgIcon,
  orgIconWhite,
  partsIconBlue,
  partsIconGrey,
  primaryLinkIcon,
  profileIcon,
  provisionIcon,
  provisionIconWhite,
  pumpIcon,
  reminderIcon,
  reportingIcon,
  searchIcon,
  searchIconLg,
  signalIcon,
  signalIconWhite,
  usersIcon,
  usersIconWhite,
  widgetIcon,
};

const mapLibraries = ['drawing', 'geometry'];
const initState = {
  currentOrg: {},
  currentUser: {},
  error: '',
  nextOrgName: '',
  orgsMap: {},
  rootOrg: {},
  searchTerm: '',
  showOverlay: false,
  sidebarCollapsed: false,
  width: window.innerWidth,
};

export default function Providers({ branding }) {
  const [state, updateState] = useState(initState);

  const history = useHistory(),
    location = useLocation();

  const targetPath = useMemo(
    () =>
      location.pathname === '/login'
        ? ''
        : `${location.pathname}${location.search}`,
    [location.pathname, location.search]
  );
  const loginRedirect = useMemo(
    () => ({ pathname: '/login', state: { targetPath } }),
    [targetPath]
  );

  const accountService = useMemo(() => new AccountService(), []),
    api = useMemo(() => new API(), []),
    auth = useMemo(() => new AuthService(), []),
    isDesktop = useMemo(() => state.width >= LARGE_WIDTH_PX, [state.width]),
    localStorageService = useMemo(() => new LocalStorageService(), []),
    orgService = useMemo(() => new OrgService(), []);

  let source = CancelToken.source();

  const exitSubOrg = async (nextOrgID, nextOrgName) => {
    updateState((prevState) => ({
      ...prevState,
      nextOrgName,
      showOverlay: true,
    }));
    auth.setCurrentOrgID(nextOrgID);

    if (nextOrgID === state.rootOrg?._id) {
      return updateState((prevState) => ({
        ...prevState,
        currentOrg: state.rootOrg,
        showOverlay: false,
      }));
    }

    try {
      const nextOrg = await orgService.fetchFullOrgDetails(
        nextOrgID,
        true,
        source.token
      );
      updateState((prevState) => ({
        ...prevState,
        currentOrg: nextOrg,
        showOverlay: false,
      }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        currentOrg: state.rootOrg,
        showOverlay: false,
      }));
    }
  };

  const handleSearchTerm = ({ target: { value: searchTerm } }) => {
    updateState((prevState) => ({ ...prevState, searchTerm }));
  };

  const handleSubOrgDelete = async () => {
    try {
      const rootOrgID = auth.getUserRootOrgID();
      const currentOrgID = auth.getCurrentOrgID();
      const currentlyViewingRoot = rootOrgID === currentOrgID;

      const rootOrgRequest = orgService.fetchFullOrgDetails(
        rootOrgID,
        true,
        source.token
      );

      let currentOrgRequest;
      if (!currentlyViewingRoot) {
        currentOrgRequest = orgService.fetchFullOrgDetails(
          currentOrgID,
          true,
          source.token
        );
      }

      const [rootOrg, currentOrg] = await Promise.all([
        rootOrgRequest,
        currentOrgRequest,
      ]);
      const orgsMap = generateOrgsMap(rootOrg);
      updateState((prevState) => ({
        ...prevState,
        orgsMap,
        rootOrg,
        currentOrg: currentlyViewingRoot ? rootOrg : currentOrg,
      }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({ ...prevState, error }));
    }
  };

  const resetStateOnLogout = useCallback(() => {
    updateState((prevState) => ({
      ...prevState,
      rootOrg: {},
      currentOrg: {},
      currentUser: {},
      orgsMap: {},
    }));
  }, []);

  const setCurrentOrg = async (org) => {
    if (Object.keys(org || {})?.length) {
      return updateState((prevState) => ({ ...prevState, currentOrg: org }));
    }

    try {
      const currentOrg = await orgService.fetchFullOrgDetails(
        auth.getCurrentOrgID(),
        true,
        source.token
      );

      updateState((prevState) => ({ ...prevState, currentOrg }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({ ...prevState, error }));
    }
  };

  const toggleSidebarCollapsed = useCallback(() => {
    updateState((prevState) => ({
      ...prevState,
      sidebarCollapsed: !prevState.sidebarCollapsed,
    }));
  }, []);

  const updateCurrentOrg = useCallback((orgUpdate, newOrg) => {
    updateState((prevState) => {
      const currentOrg = { ...prevState.currentOrg, ...orgUpdate };
      const orgsMap = newOrg
        ? {
            ...prevState.orgsMap,
            [newOrg._id]: {
              logo: newOrg.logo,
              name: newOrg.name,
              path: newOrg.path,
            },
          }
        : prevState.orgsMap;
      return { ...prevState, currentOrg, orgsMap };
    });
  }, []);

  const updateCurrentUser = useCallback((userUpdate) => {
    updateState((prevState) => {
      const currentUser = { ...prevState.currentUser, ...userUpdate };
      return { ...prevState, currentUser };
    });
  }, []);

  const updateWindowDimensions = useCallback(() => {
    updateState((prevState) => ({ ...prevState, width: window.innerWidth }));
  }, []);

  const _componentCleanup = () => {
    window.removeEventListener('resize', updateWindowDimensions);
    source.cancel();
  };

  const _setInitState = async () => {
    const NO_SESSION_TOKEN_MSG = 'session token not set';
    const ROOT_ORG_ID_NOT_SET_MSG = 'rootOrgID not set';
    const CURRENT_ORG_ID_NOT_SET_MSG = 'currentOrgID not set';

    try {
      const currentOrgID = auth.getCurrentOrgID();
      const rootOrgID = auth.getUserRootOrgID();
      const currentOrgIsRoot = currentOrgID === rootOrgID;

      const currentUserReq = Object.keys(state.currentUser || {})?.length
        ? state.currentUser
        : !auth.getToken()
          ? generatePromiseError(NO_SESSION_TOKEN_MSG)
          : accountService.fetchCurrentUserDetails(source.token);

      const rootOrgReq = Object.keys(state.rootOrg || {})?.length
        ? state.rootOrg
        : !rootOrgID
          ? generatePromiseError(ROOT_ORG_ID_NOT_SET_MSG)
          : orgService.fetchFullOrgDetails(rootOrgID, true, source.token);

      const currentOrgReq = Object.keys(state.currentOrg || {})?.length
        ? state.currentOrg
        : !currentOrgID
          ? generatePromiseError(CURRENT_ORG_ID_NOT_SET_MSG)
          : currentOrgIsRoot
            ? null
            : orgService.fetchFullOrgDetails(currentOrgID, true, source.token);

      const [currentUser, rootOrg, currentOrg] = await Promise.all([
        currentUserReq,
        rootOrgReq,
        currentOrgReq,
      ]);
      const orgsMap = generateOrgsMap(rootOrg);

      const permissions = localStorageService.getObjectByKey('permissions');
      if (!permissions) {
        await api.request(auth.buildLogoutRequest());
        auth.logout();
        history.replace('/login');
      }

      updateState((prevState) => ({
        ...prevState,
        currentOrg: currentOrgIsRoot ? rootOrg : currentOrg,
        currentUser,
        orgsMap,
        permissions,
        rootOrg,
      }));
    } catch (err) {
      if (isCancel(err)) return;

      if (
        err.response?.status === 401 ||
        err.message === NO_SESSION_TOKEN_MSG
      ) {
        auth.logout();
        history.replace(loginRedirect);
      } else {
        const error = err.response?.data?.message ?? err.message;
        updateState((prevState) => ({ ...prevState, error }));
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', updateWindowDimensions);
    _setInitState();
    return _componentCleanup;
  }, []);

  return (
    <ErrorBoundary>
      <BrandingProvider value={branding}>
        <RootOrgProvider value={state.rootOrg}>
          <OrgsMapProvider value={state.orgsMap}>
            <PermissionsProvider value={state.permissions}>
              <CurrentOrgProvider value={state.currentOrg}>
                <CurrentUserProvider value={state.currentUser}>
                  <UpdateCurrentOrgProvider value={updateCurrentOrg}>
                    <SetCurrentOrgProvider value={setCurrentOrg}>
                      <UpdateCurrentUserProvider value={updateCurrentUser}>
                        <ExitSubOrgProvider value={exitSubOrg}>
                          <HandleSubOrgDeleteProvider
                            value={handleSubOrgDelete}
                          >
                            <ToggleSidebarContextProvider
                              value={toggleSidebarCollapsed}
                            >
                              <UserProvider value={state.currentUser}>
                                <WidthProvider value={state.width}>
                                  <BreakpointProvider>
                                    <ResetGlobalStateProvider
                                      value={resetStateOnLogout}
                                    >
                                      <IconsProvider
                                        value={{
                                          ...images,
                                          assetIcon: branding?.asset_icon,
                                          eventsHeaderDesktop:
                                            branding?.events_banner_desktop_url,
                                          fleetHeaderDesktop:
                                            branding?.fleet_banner_desktop_url,
                                          inventoryHeaderDesktop:
                                            branding?.inventory_banner_desktop_url,
                                          inventoryHeaderMobile:
                                            branding?.inventory_banner_mobile_url,
                                          loginBackgroundImage:
                                            branding?.login_background_url,
                                          reportsHeaderDesktop:
                                            branding?.reports_banner_desktop_url,
                                          reportsHeaderMobile:
                                            branding?.reports_banner_mobile_url,
                                          tigeLoginBackgroundImage:
                                            branding?.tige_login_background_url,
                                        }}
                                      >
                                        <LoadScript
                                          googleMapsApiKey={
                                            process.env.GOOGLE_MAPS_API_KEY
                                          }
                                          libraries={mapLibraries}
                                          loadingElement={
                                            <div
                                              className="w-100 d-flex align-items-center justify-content-center"
                                              style={{
                                                height: isDesktop
                                                  ? MAIN_HEIGHT
                                                  : MOBILE_MAIN_HEIGHT,
                                              }}
                                            >
                                              <BrandPrimarySpinningCircle size="5x" />
                                            </div>
                                          }
                                        >
                                          <Routes
                                            {...state}
                                            handleLoginSuccess={_setInitState}
                                            handleSearchTerm={handleSearchTerm}
                                            missingImage={missingImage}
                                            profileIcon={profileIcon}
                                            setCurrentOrg={setCurrentOrg}
                                            updateCurrentUser={
                                              updateCurrentUser
                                            }
                                          />
                                        </LoadScript>
                                      </IconsProvider>
                                    </ResetGlobalStateProvider>
                                  </BreakpointProvider>
                                </WidthProvider>
                              </UserProvider>
                            </ToggleSidebarContextProvider>
                          </HandleSubOrgDeleteProvider>
                        </ExitSubOrgProvider>
                      </UpdateCurrentUserProvider>
                    </SetCurrentOrgProvider>
                  </UpdateCurrentOrgProvider>
                </CurrentUserProvider>
              </CurrentOrgProvider>
            </PermissionsProvider>
          </OrgsMapProvider>
        </RootOrgProvider>
      </BrandingProvider>
    </ErrorBoundary>
  );
}

Providers.propTypes = { branding: PropTypes.object };

function generateOrgsMap(rootOrg) {
  if (!Array.isArray(rootOrg?.children)) return {};

  return rootOrg.children
    .concat(rootOrg)
    .reduce((map, { _id, features, gatewayCount, logo, name, path }) => {
      map[_id] = { features, gatewayCount, logo, name, path };
      return map;
    }, {});
}

function generatePromiseError(msg) {
  return new Promise((_, reject) => reject(new Error(msg)));
}
